import React, { useMemo, useState } from 'react';
import { Box, Button, Checkbox, Chip, FormControlLabel, Paper, Stack, TextField, Typography } from '@mui/material';
import { ICatalog, ICatalogModule } from '../../interfaces/role.interface';

interface Props { catalog: ICatalog; selected: Set<string>; onChange: (next: Set<string>) => void; readOnly?: boolean; }

const PermissionTree: React.FC<Props> = ({ catalog, selected, onChange, readOnly }) => {
    const [filter, setFilter] = useState('');
    const q = filter.trim().toLowerCase();
    const visible = useMemo(() => catalog.modules.map(m => ({
        ...m, permissions: m.permissions.filter(p => !q || p.key.toLowerCase().includes(q) || p.label.toLowerCase().includes(q)),
    })).filter(m => m.permissions.length > 0), [catalog, q]);

    const toggle = (key: string) => { const next = new Set(selected); if (next.has(key)) next.delete(key); else next.add(key); onChange(next); };
    const selectAll = (keys: string[]) => { const next = new Set(selected); keys.forEach(k => next.add(k)); onChange(next); };
    const groupsOf = (m: ICatalogModule) => {
        const names = Array.from(new Set(m.permissions.map(p => p.group)));
        return [...names.filter(g => g === 'Navigation'), ...names.filter(g => g !== 'Navigation')];
    };

    return (
        <Stack spacing={2} data-help-section="roles.editor.section.permissions">
            <TextField size="small" data-help="roles.editor.permissions.filter" placeholder="Filter features and actions" value={filter} onChange={e => setFilter(e.target.value)} />
            {visible.map(m => (
                <Paper key={m.key} variant="outlined" sx={{ p: 2, opacity: m.enabled ? 1 : 0.6 }} data-help-section="roles.editor.permissions.module">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="subtitle1">{m.label}</Typography>
                        {m.enabled
                            ? !readOnly && <Button size="small" data-help="roles.editor.permissions.selectModule" aria-label={`Select all ${m.label}`} onClick={() => selectAll(m.permissions.map(p => p.key))}>Select all</Button>
                            : <Chip size="small" label="Not enabled for this institution" />}
                    </Box>
                    {groupsOf(m).map(g => (
                        <Box key={g} sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="caption" sx={{ letterSpacing: 0.5 }}>{g.toUpperCase()}</Typography>
                                {m.enabled && !readOnly && (
                                    <Button size="small" data-help="roles.editor.permissions.selectGroup" aria-label={`Select all in ${g}`} onClick={() => selectAll(m.permissions.filter(p => p.group === g).map(p => p.key))}>Select all</Button>
                                )}
                            </Box>
                            {m.permissions.filter(p => p.group === g).map(p => (
                                <Box key={p.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FormControlLabel label={p.label} data-help="roles.editor.permissions.permission" control={
                                        <Checkbox checked={selected.has(p.key)} disabled={!m.enabled || readOnly} onChange={() => toggle(p.key)} inputProps={{ 'aria-label': p.label }} />
                                    } />
                                    <Chip size="small" variant="outlined" label={p.navigation ? 'UI' : 'Action'} />
                                    {p.scopeable && <Chip size="small" variant="outlined" label="scope" />}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Paper>
            ))}
        </Stack>
    );
};
export default PermissionTree;
